<template>
  <UIDrawer
    :is-open
    :z-index
    :title="name"
    size="lg"
    :allow-tray
    @minimize="handleMinimize"
    @close="handleClose"
  >
    <template #title>
      <div class="account-connector__header">
        <AccountConnectorTitle v-bind="{ type, name }" />
        <div
          v-if="connectorLogo"
          class="account-connector__header-powered-by"
        >
          <span>Powered by</span>
          <img :src="connectorLogo" alt="" />
        </div>
      </div>
    </template>
    <AccountConnectorInfo
      v-bind="{ account, connector }"
      :loading="isLoading"
    />
    <UITabs v-model="tab" :tabs class="account-connector__tabs" />
    <div
      v-if="isInitTab.Transactions"
      v-show="tab === TABS.TRANSACTIONS"
      class="account-connector"
    >
      <AccountConnectorTransactions v-bind="{ assetId }" />
    </div>
    <div
      v-if="isInitTab.Positions"
      v-show="tab === TABS.POSITIONS"
      class="account-connector"
    >
      <AccountConnectorPositions v-bind="{ linkedAccountId }" />
    </div>
  </UIDrawer>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { useDark } from '@vueuse/core'

import { LinkedDataAccount, ModalClass } from '@types'
import { AssetClass } from '..'

import { ASSET_FIELD } from '../utils/const'

import { useLinkedDataStore } from '@/store/linkedData'
import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'

import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'

import { UITabs } from '@ui/core'
import { UIDrawer } from '@ui/modals'
import AccountConnectorInfo from './AccountConnectorInfo.vue'
import AccountConnectorPositions from './AccountConnectorPositions.vue'
import AccountConnectorTitle from './AccountConnectorTitle.vue'
import AccountConnectorTransactions from './AccountConnectorTransactions.vue'

enum TABS {
  TRANSACTIONS = 'Transactions',
  POSITIONS = 'Positions',
}

type Props = {
  modal: ModalClass<AssetClass>
  instance: AssetClass
  assetId?: string
}

const props = defineProps<Props>()

const isDark = useDark()

const linkedDataStore = useLinkedDataStore()
const linkedDataConnectorsStore = useLinkedDataConnectorsStore()

const { syncing } = useLinkedDataConnector()

const type = props.instance.field<string>(ASSET_FIELD.TYPE)
const name = props.instance.field<string>(ASSET_FIELD.NAME)

const isLoading = ref(false)
const account = ref<LinkedDataAccount>()

const tab = ref<TABS>(TABS.TRANSACTIONS)

const isInitTab = reactive<{ [key in TABS]: boolean }>({
  [TABS.TRANSACTIONS]: false,
  [TABS.POSITIONS]: false,
})

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)
const allowTray = computed(() => props.modal.allowTray)

const linkedAccountId = computed(
  () =>
    props.instance.field<string | null>(ASSET_FIELD.LINKED_ACCOUNT_ID).value,
)

const tabs = computed(() =>
  Object.values(TABS).map(name => ({
    name,
  })),
)

const connector = computed(() =>
  linkedDataConnectorsStore.getList.find(
    connector => connector.id === account.value?.connector_id,
  ),
)

const connectorLogo = computed(() => {
  switch (connector.value?.source) {
    case 'carta':
      return `/assets/img/connectors/carta${isDark.value ? '-light' : ''}.svg`
    default:
      return ''
  }
})

const handleClose = () => {
  props.modal.close()
}

const handleMinimize = () => {
  props.modal.hide()
}

const getAccount = async () => {
  if (!linkedAccountId.value) return
  isLoading.value = true
  account.value = await linkedDataStore.getAccount(linkedAccountId.value)
  isLoading.value = false
}

watch(syncing, value => {
  if (value) return
  getAccount()
})

watch(
  tab,
  value => {
    isInitTab[value] = true
  },
  { immediate: true },
)

onBeforeMount(async () => {
  if (!linkedDataConnectorsStore.initFlag) {
    linkedDataConnectorsStore.fetch()
  }
  getAccount()
})
</script>

<style>
.account-connector {
  @apply flex flex-col;
  @apply flex-auto;

  &__header {
    @apply flex flex-col sm:flex-row items-start sm:items-center;

    .account-connector-title {
      @apply flex-auto;
    }
  }

  &__header-powered-by {
    @apply sm:mr-4;
    @apply flex items-center;
    @apply text-base sm:text-sm;
    @apply whitespace-nowrap;

    img {
      @apply w-20;
    }
  }

  &__tabs {
    @apply w-auto;
  }

  .ui-grid--wrapped {
    @apply -mx-6;
  }
}
</style>
